import React from 'react';
import { Link } from 'react-router-dom';
import { FiFileText, FiShoppingBag, FiCreditCard, FiTruck, FiRefreshCw, FiShield, FiAlertCircle, FiX, FiCheck, FiGift, FiLock, FiMail } from 'react-icons/fi';
import SEO from '../components/SEO';
import { trackEvent } from '../components/GoogleAnalytics';
import { useEffect } from 'react';

export default function Terms() {
  useEffect(() => {
    trackEvent('page_view', 'navigation', 'terms_of_service');
  }, []);

  return (
    <>
      <SEO
        title="Terms of Service - Bailebelle"
        description="Bailebelle's terms and conditions. Review our terms of service for using our website and purchasing products."
        keywords="terms of service, terms and conditions, legal, user agreement"
        url="/terms"
      />
      <div className="min-h-screen bg-black text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4" style={{ fontFamily: 'serif' }}>
              Terms of Service
            </h1>
            <p className="text-gray-400 text-lg max-w-3xl mx-auto">
              Please read these terms carefully before using our website or making a purchase. By using our services, you agree to be bound by these terms.
            </p>
          </div>

          {/* Introduction */}
          <div className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-2xl p-8 mb-12">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                <FiFileText className="text-pink-500 mt-1" size={32} />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white mb-4">Agreement to Terms</h2>
                <p className="text-gray-300 text-lg leading-relaxed">
                  These Terms of Service ("Terms") govern your access to and use of the Bailebelle website (the "Service")
                  operated by Bailebelle ("we," "us," or "our"). By accessing or using our Service, you agree to be bound
                  by these Terms. If you disagree with any part of these Terms, you may not access or use the Service.
                </p>
              </div>
            </div>
          </div>

          {/* Acceptance of Terms */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
            <div className="bg-gray-900 rounded-2xl p-6">
              <div className="flex items-start space-x-4 mb-4">
                <div className="flex-shrink-0">
                  <FiCheck className="text-green-500 mt-1" size={24} />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">By Using Our Service</h3>
                  <p className="text-gray-400 text-sm mb-3">
                    By accessing, browsing, or using our website, you acknowledge that you have read, understood, and agree
                    to be bound by these Terms and to comply with all applicable laws and regulations.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gray-900 rounded-2xl p-6">
              <div className="flex items-start space-x-4 mb-4">
                <div className="flex-shrink-0">
                  <FiAlertCircle className="text-yellow-500 mt-1" size={24} />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">Updates to Terms</h3>
                  <p className="text-gray-400 text-sm mb-3">
                    We reserve the right to update, change, or replace any part of these Terms at any time. It is your
                    responsibility to check this page periodically for changes. Your continued use of the Service
                    constitutes your acceptance of any changes.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Use of Service */}
          <div className="mb-16">
            <h2 className="text-2xl font-bold text-white mb-6 text-center">Use of Our Service</h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-gray-900 rounded-2xl p-6">
                <div className="flex items-start space-x-4 mb-4">
                  <div className="flex-shrink-0">
                    <FiCheck className="text-green-500 mt-1" size={24} />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-2">Permitted Uses</h3>
                    <p className="text-gray-400 mb-3 text-sm">You may use our Service for lawful purposes and in accordance with these Terms:</p>
                    <ul className="text-gray-400 space-y-1 text-sm">
                      <li className="flex items-start">
                        <FiCheck className="text-green-500 mt-1 mr-2 flex-shrink-0" size={18} />
                        <span>Browse and view products and collections</span>
                      </li>
                      <li className="flex items-start">
                        <FiCheck className="text-green-500 mt-1 mr-2 flex-shrink-0" size={18} />
                        <span>Make legitimate purchases</span>
                      </li>
                      <li className="flex items-start">
                        <FiCheck className="text-green-500 mt-1 mr-2 flex-shrink-0" size={18} />
                        <span>Access information about our services</span>
                      </li>
                      <li className="flex items-start">
                        <FiCheck className="text-green-500 mt-1 mr-2 flex-shrink-0" size={18} />
                        <span>Contact our customer service team</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="bg-gray-900 rounded-2xl p-6">
                <div className="flex items-start space-x-4 mb-4">
                  <div className="flex-shrink-0">
                    <FiX className="text-red-500 mt-1" size={24} />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-2">Prohibited Uses</h3>
                    <p className="text-gray-400 mb-3 text-sm">You may not use our Service for any illegal or unauthorized purpose:</p>
                    <ul className="text-gray-400 space-y-1 text-sm">
                      <li className="flex items-start">
                        <FiX className="text-red-500 mt-1 mr-2 flex-shrink-0" size={18} />
                        <span>Violate any laws or regulations</span>
                      </li>
                      <li className="flex items-start">
                        <FiX className="text-red-500 mt-1 mr-2 flex-shrink-0" size={18} />
                        <span>Attempt to gain unauthorized access</span>
                      </li>
                      <li className="flex items-start">
                        <FiX className="text-red-500 mt-1 mr-2 flex-shrink-0" size={18} />
                        <span>Introduce viruses or malicious code</span>
                      </li>
                      <li className="flex items-start">
                        <FiX className="text-red-500 mt-1 mr-2 flex-shrink-0" size={18} />
                        <span>Collect user information without consent</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Products and Pricing */}
          <div className="mb-16">
            <h2 className="text-2xl font-bold text-white mb-6 text-center">Products and Pricing</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-gray-900 rounded-2xl p-6 text-center">
                <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FiShoppingBag className="text-white" size={28} />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">Product Information</h3>
                <p className="text-gray-400 text-sm">
                  We strive to provide accurate descriptions and images of our products. However, colors may vary due to
                  display settings, and specifications are subject to change without notice.
                </p>
              </div>
              <FiGift className="text-white" size={28} />
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">Sales & Promotions</h3>
            <p className="text-gray-400 text-sm">
              We may offer promotions, discounts, or special offers at our discretion. Promotions are subject to
              availability and may be modified or discontinued without notice.
            </p>
          </div>


          {/* Orders and Payments */}
          <div className="mb-16">
            <h2 className="text-2xl font-bold text-white mb-6 text-center">Orders and Payment</h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-gray-900 rounded-2xl p-6">
                <div className="flex items-start space-x-4 mb-4">
                  <div className="flex-shrink-0">
                    <FiCheck className="text-blue-500 mt-1" size={24} />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-2">Order Acceptance</h3>
                    <p className="text-gray-400 text-sm mb-3">
                      When you place an order, we will send you an order confirmation. Your order is an offer to purchase,
                      which we accept when we send the confirmation email.
                    </p>
                    <ul className="text-gray-400 space-y-1 text-sm">
                      <li>• We reserve the right to reject or cancel any order</li>
                      <li>• In case of cancellation, you will receive a full refund</li>
                      <li>• We may limit quantities per person or per order</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="bg-gray-900 rounded-2xl p-6">
                <div className="flex items-start space-x-4 mb-4">
                  <div className="flex-shrink-0">
                    <FiLock className="text-green-500 mt-1" size={24} />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-2">Payment Security</h3>
                    <p className="text-gray-400 text-sm mb-3">
                      We accept various payment methods including credit cards, debit cards, and other secure payment options.
                      All payment information is encrypted and processed securely through our payment processors.
                    </p>
                    <ul className="text-gray-400 space-y-1 text-sm">
                      <li>• Payments are processed securely via SSL encryption</li>
                      <li>• We never store your full payment card details</li>
                      <li>• All transactions are processed by PCI-DSS compliant providers</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Shipping and Delivery */}
          <div className="mb-16">
            <h2 className="text-2xl font-bold text-white mb-6 text-center">Shipping and Delivery</h2>
            <div className="bg-gray-900 rounded-2xl p-6">
              <div className="flex items-start space-x-4 mb-6">
                <div className="flex-shrink-0">
                  <FiTruck className="text-cyan-500 mt-1" size={24} />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white mb-3">Delivery Information</h3>
                  <p className="text-gray-400 text-sm mb-4">
                    We ship to addresses within the United States. Delivery times vary by location and shipping method
                    selected at checkout. Estimated delivery times are provided but not guaranteed.
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-white font-semibold mb-2 text-sm">Shipping Options:</p>
                      <ul className="text-gray-400 space-y-1 text-sm">
                        <li>• Standard shipping: 5-7 business days</li>
                        <li>• Express shipping: 2-3 business days</li>
                        <li>• Overnight shipping available for select areas</li>
                      </ul>
                    </div>
                    <div>
                      <p className="text-white font-semibold mb-2 text-sm">Important Notes:</p>
                      <ul className="text-gray-400 space-y-1 text-sm">
                        <li>• Tracking information provided via email</li>
                        <li>• Signature may be required for deliveries</li>
                        <li>• Shipments may be delayed due to weather or carrier issues</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Returns and Refunds */}
          <div className="mb-16">
            <h2 className="text-2xl font-bold text-white mb-6 text-center">Returns and Refunds</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-gray-900 rounded-2xl p-6">
                <div className="flex items-start space-x-4 mb-4">
                  <div className="flex-shrink-0">
                    <FiRefreshCw className="text-pink-500 mt-1" size={24} />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-2">Return Policy</h3>
                    <p className="text-gray-400 text-sm mb-3">
                      We offer free returns within 30 days of delivery. Items must be unworn, unwashed, and in original
                      condition with tags attached.
                    </p>
                    <p className="text-pink-400 text-xs font-semibold">
                      For detailed return information, please visit our Returns & Exchanges page.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gray-900 rounded-2xl p-6">
                <div className="flex items-start space-x-4 mb-4">
                  <div className="flex-shrink-0">
                    <FiRefreshCw className="text-green-500 mt-1" size={24} />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-2">Refund Processing</h3>
                    <p className="text-gray-400 text-sm mb-3">
                      Refunds are processed to the original payment method within 5-7 business days after we receive
                      your returned item. You will receive an email confirmation once your refund is processed.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Intellectual Property */}
          <div className="bg-gray-900 rounded-2xl p-6 mb-12">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                <FiShield className="text-purple-500 mt-1" size={24} />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white mb-3">Intellectual Property Rights</h3>
                <p className="text-gray-400 text-sm mb-4">
                  All content on this website, including text, graphics, logos, images, software, and the compilation thereof
                  is the property of Bailebelle or its suppliers and is protected by copyright, trademark, and other laws.
                  You may not reproduce, distribute, modify, or create derivative works without our written permission.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <div>
                    <p className="text-white font-semibold mb-2 text-sm">You May:</p>
                    <ul className="text-gray-400 space-y-1 text-sm">
                      <li>• Use website content for personal, non-commercial use</li>
                      <li>• Share links to our website on social media</li>
                      <li>• Print pages for personal reference</li>
                    </ul>
                  </div>
                  <div>
                    <p className="text-white font-semibold mb-2 text-sm">You May Not:</p>
                    <ul className="text-gray-400 space-y-1 text-sm">
                      <li>• Copy content for commercial use</li>
                      <li>• Remove copyright or trademark notices</li>
                      <li>• Use our logos without permission</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Limitation of Liability */}
          <div className="bg-gray-900 rounded-2xl p-6 mb-12">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                <FiAlertCircle className="text-orange-500 mt-1" size={24} />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white mb-3">Limitation of Liability</h3>
                <p className="text-gray-400 text-sm mb-4">
                  To the fullest extent permitted by law, Bailebelle and its directors, employees, agents, and affiliates
                  shall not be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss
                  of profits or revenues, whether incurred directly or indirectly, or any loss of data, use, goodwill, or
                  other intangible losses resulting from your use of the Service.
                </p>
                <p className="text-orange-400 text-xs font-semibold">
                  This limitation applies regardless of the legal theory on which your claim is based, including but not
                  limited to contract, tort, or otherwise.
                </p>
              </div>
            </div>
          </div>

          {/* Governing Law */}
          <div className="bg-gray-900 rounded-2xl p-6 mb-12">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                <FiFileText className="text-blue-500 mt-1" size={24} />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white mb-3">Governing Law and Jurisdiction</h3>
                <p className="text-gray-400 text-sm">
                  These Terms shall be governed by and construed in accordance with the laws of the United States, without
                  regard to its conflict of law provisions. Any disputes arising out of or relating to these Terms or the
                  Service shall be resolved in the courts located in the United States. You agree to submit to the exclusive
                  jurisdiction of such courts.
                </p>
              </div>
            </div>
          </div>

          {/* Contact & Links */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            <div className="bg-gradient-to-r from-blue-900/30 to-blue-800/30 rounded-2xl p-6">
              <div className="flex items-start space-x-4">
                <FiRefreshCw className="text-blue-500 mt-1 flex-shrink-0" size={24} />
                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">Returns</h3>
                  <p className="text-gray-400 text-sm mb-3">Learn about our returns and exchange policy.</p>
                  <Link
                    to="/returns"
                    className="text-blue-400 hover:text-blue-300 transition-colors text-sm font-semibold"
                  >
                    View Policy →
                  </Link>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-pink-900/30 to-purple-800/30 rounded-2xl p-6">
              <div className="flex items-start space-x-4">
                <FiShield className="text-pink-500 mt-1 flex-shrink-0" size={24} />
                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">Privacy</h3>
                  <p className="text-gray-400 text-sm mb-3">Review how we protect your personal data.</p>
                  <Link
                    to="/privacy"
                    className="text-pink-400 hover:text-pink-300 transition-colors text-sm font-semibold"
                  >
                    Privacy Policy →
                  </Link>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-green-900/30 to-emerald-800/30 rounded-2xl p-6">
              <div className="flex items-start space-x-4">
                <FiMail className="text-green-500 mt-1 flex-shrink-0" size={24} />
                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">Support</h3>
                  <p className="text-gray-400 text-sm mb-3">Need help? Contact our support team.</p>
                  <Link
                    to="/contact"
                    className="text-green-400 hover:text-green-300 transition-colors text-sm font-semibold"
                  >
                    Contact Us →
                  </Link>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Section */}
          <div className="text-center">
            <div className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-2xl p-8">
              <h2 className="text-2xl font-bold text-white mb-4">Questions About These Terms?</h2>
              <p className="text-gray-400 mb-6">
                If you have any questions or concerns about these Terms of Service, please contact us.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
                <Link
                  to="/contact"
                  className="inline-flex items-center px-6 py-3 bg-pink-500 text-white font-semibold rounded-lg hover:bg-pink-600 transition-colors"
                >
                  <FiMail className="mr-2" size={18} />
                  Contact Us
                </Link>
                <Link
                  to="/privacy"
                  className="inline-flex items-center px-6 py-3 border-2 border-gray-600 text-white font-semibold rounded-lg hover:border-pink-500 transition-colors"
                >
                  Privacy Policy
                </Link>
              </div>
              <p className="text-gray-500 text-sm mt-4">
                Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
              </p>
            </div>
          </div>
        </div >
      </div >
    </>
  );
}
